/**
 * IndividualEditor.jsx — Admin/RN Individual management surface (owner module).
 *
 * Wraps the existing tenant-scoped, object-authorized, audit-logged endpoints:
 *   PATCH  /api/individuals/{iid}
 *   POST   /api/individuals/{iid}/emergency-contacts        (replace-list)
 *   POST   /api/individuals/{iid}/external-identifiers      (add)
 *   POST   /api/individuals/{iid}/external-identifiers/{id}/deprecate
 *   POST   /api/individuals/{iid}/service-authorizations    (replace-list)
 *   GET    /api/individuals/{iid}/assigned-dsps
 *   POST   /api/individuals/{iid}/authorize-dsp
 *   DELETE /api/individuals/{iid}/authorize-dsp/{dsp_id}
 *   POST   /api/individuals/{iid}/discharge|reactivate|mark-deceased
 *
 * Design contract (do not violate without an ADR):
 *   - ADR-0012 — this component belongs to the Individuals module; the
 *                Workspace remains read-only and merely deep-links here.
 *   - Principle #9 — external identifier `value` is never mutated;
 *                    only add + deprecate.
 *   - Replace-list endpoints are pre-populated with the current server
 *                    state before edits so no silent deletion can occur.
 *   - DOB is deliberately not editable here.
 *   - Admin/RN only. DSP callers cannot open this surface.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Plus, Trash2, AlertTriangle, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "../lib/api";

const TABS = [
  { k: "demographics",    label: "Demographics" },
  { k: "contacts",        label: "Emergency contacts" },
  { k: "identifiers",     label: "Identifiers" },
  { k: "authorizations",  label: "Service authorizations" },
  { k: "dsps",            label: "Authorized DSPs" },
  { k: "status",          label: "Status" },
];

// ADR-0016 helper — collapse an empty address object to null so the
// server does not persist a placeholder record masquerading as filled.
const addrOrNull = (a) => (a && Object.values(a).some((v) => v && String(v).trim())) ? a : null;
const emptyAddr = () => ({ street: "", unit: "", city: "", state: "", zip: "", county: "" });

export default function IndividualEditor({ individual, onClose, initialTab = "demographics", onSaved }) {
  const [tab, setTab] = useState(initialTab);
  const [fresh, setFresh] = useState(null);      // full record loaded from server
  const [loading, setLoading] = useState(true);

  const reload = () =>
    api.get(`/individuals/${individual.id}`).then((r) => {
      setFresh(r.data);
      setLoading(false);
    }).catch((e) => {
      toast.error(formatApiError(e));
      setLoading(false);
    });

  useEffect(() => { setLoading(true); reload(); }, [individual.id]);

  const bumpSaved = async () => {
    await reload();
    if (onSaved) onSaved();
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      data-testid="individual-editor-modal">
      <div className="accp-card w-full max-w-4xl max-h-[92vh] p-0 overflow-hidden flex flex-col">
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div>
            <div className="accp-label">Manage individual</div>
            <h2 className="text-2xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}>
              {individual.last_name}, {individual.first_name}
            </h2>
            <div className="font-mono text-xs text-muted-foreground mt-1">{individual.code}</div>
          </div>
          <button onClick={onClose} data-testid="editor-close-btn"
            className="p-1 hover:bg-slate-100 rounded"><X size={20} /></button>
        </div>
        <div className="border-b border-border px-6 flex gap-2 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.k}
              className={`px-3 py-2 text-sm border-b-2 transition-colors whitespace-nowrap ${tab === t.k ? "border-primary text-primary font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              onClick={() => setTab(t.k)}
              data-testid={`editor-tab-${t.k}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="p-6 overflow-y-auto flex-1">
          {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
          {!loading && fresh && tab === "demographics" && (
            <DemographicsTab row={fresh} onSaved={bumpSaved} />
          )}
          {!loading && fresh && tab === "contacts" && (
            <ContactsTab row={fresh} onSaved={bumpSaved} />
          )}
          {!loading && fresh && tab === "identifiers" && (
            <IdentifiersTab row={fresh} onSaved={bumpSaved} />
          )}
          {!loading && fresh && tab === "authorizations" && (
            <AuthorizationsTab row={fresh} onSaved={bumpSaved} />
          )}
          {!loading && fresh && tab === "dsps" && (
            <DspsTab row={fresh} onSaved={bumpSaved} />
          )}
          {!loading && fresh && tab === "status" && (
            <StatusTab row={fresh} onSaved={bumpSaved} />
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Demographics — PATCH /api/individuals/{iid} with the subset of fields the
// backend already accepts on IndividualUpdate. DOB is deliberately absent
// per operator directive.
// ---------------------------------------------------------------------------
function DemographicsTab({ row, onSaved }) {
  const [f, setF] = useState({
    first_name:        row.first_name || "",
    last_name:         row.last_name || "",
    preferred_name:    row.preferred_name || "",
    primary_language:  row.primary_language || "",
    guardian_type:     row.guardian_type || "self",
    county:            row.county || "",
    mobility_notes:    row.mobility_notes || "",
    communication_notes: row.communication_notes || "",
    phone:             row.phone || "",
    email:             row.email || "",
    home_address:      row.home_address || emptyAddr(),
    mailing_same:      !row.mailing_address
                        || JSON.stringify(row.mailing_address) === JSON.stringify(row.home_address),
    mailing_address:   row.mailing_address || emptyAddr(),
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        first_name:        f.first_name.trim() || null,
        last_name:         f.last_name.trim() || null,
        preferred_name:    f.preferred_name.trim() || null,
        primary_language:  f.primary_language.trim() || null,
        guardian_type:     f.guardian_type,
        county:            f.county.trim() || null,
        mobility_notes:    f.mobility_notes.trim() || null,
        communication_notes: f.communication_notes.trim() || null,
        phone:             f.phone.trim() || null,
        email:             f.email.trim() || null,
        home_address:      addrOrNull(f.home_address),
        mailing_address:   f.mailing_same
                            ? addrOrNull(f.home_address)
                            : addrOrNull(f.mailing_address),
      };
      await api.patch(`/individuals/${row.id}`, payload);
      toast.success("Demographics updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="editor-demographics-form">
      <div className="text-xs text-muted-foreground">
        DOB corrections are not available here; contact an administrator if a correction is needed.
      </div>
      <div className="grid grid-cols-2 gap-4">
        <TextField label="First name"       value={f.first_name}       onChange={(v) => setF({ ...f, first_name: v })}       testid="demo-first-name" />
        <TextField label="Last name"        value={f.last_name}        onChange={(v) => setF({ ...f, last_name: v })}        testid="demo-last-name" />
        <TextField label="Preferred name"   value={f.preferred_name}   onChange={(v) => setF({ ...f, preferred_name: v })}   testid="demo-preferred-name" />
        <TextField label="Primary language" value={f.primary_language} onChange={(v) => setF({ ...f, primary_language: v })} testid="demo-primary-language" />
        <SelectField label="Guardian type"  value={f.guardian_type}    onChange={(v) => setF({ ...f, guardian_type: v })}    options={["self", "guardian", "power_of_attorney"]} testid="demo-guardian-type" />
        <TextField label="County"           value={f.county}           onChange={(v) => setF({ ...f, county: v })}           testid="demo-county" />
        <TextField label="Phone"            value={f.phone}            onChange={(v) => setF({ ...f, phone: v })}            testid="demo-phone" />
        <TextField label="Email" type="email" value={f.email}          onChange={(v) => setF({ ...f, email: v })}            testid="demo-email" />
        <TextField label="Mobility notes"   value={f.mobility_notes}   onChange={(v) => setF({ ...f, mobility_notes: v })}   span2 testid="demo-mobility-notes" />
        <TextField label="Communication notes" value={f.communication_notes} onChange={(v) => setF({ ...f, communication_notes: v })} span2 testid="demo-communication-notes" />
      </div>
      <div className="accp-label mt-4">Home address</div>
      <AddressGrid addr={f.home_address} onChange={(v) => setF({ ...f, home_address: v })} prefix="home" />
      <label className="flex items-center gap-2 mt-2 text-sm">
        <input type="checkbox" checked={f.mailing_same}
          onChange={(e) => setF({ ...f, mailing_same: e.target.checked })}
          data-testid="demo-mailing-same" />
        Mailing address is the same as home address
      </label>
      {!f.mailing_same && (
        <>
          <div className="accp-label mt-2">Mailing address</div>
          <AddressGrid addr={f.mailing_address} onChange={(v) => setF({ ...f, mailing_address: v })} prefix="mail" />
        </>
      )}
      <div className="flex justify-end pt-4 border-t">
        <button className="accp-btn-primary" disabled={saving}
          data-testid="demo-save-btn">
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Emergency contacts — replace-list. Editor loads the whole list, allows
// add / remove / edit / mark-primary, then submits the full array.
// ---------------------------------------------------------------------------
function ContactsTab({ row, onSaved }) {
  const [items, setItems] = useState(
    (row.emergency_contacts || []).map((c) => ({ ...c }))
  );
  const [saving, setSaving] = useState(false);

  const addRow = () =>
    setItems([...items, {
      id: `new-${Date.now()}`, name: "", relationship: "", phone: "",
      email: "", is_guardian: false, is_primary: items.length === 0, address: "",
    }]);
  const rmRow = (idx) =>
    setItems(items.filter((_, i) => i !== idx));
  const setField = (idx, field, value) =>
    setItems(items.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  const makePrimary = (idx) =>
    setItems(items.map((c, i) => ({ ...c, is_primary: i === idx })));

  const submit = async () => {
    // Validate: names and phones required, exactly one primary (or none if empty).
    for (const c of items) {
      if (!c.name.trim() || !c.relationship.trim() || !c.phone.trim()) {
        toast.error("Each emergency contact needs a name, relationship, and phone");
        return;
      }
    }
    const primaries = items.filter((c) => c.is_primary).length;
    if (items.length > 0 && primaries !== 1) {
      toast.error("Exactly one emergency contact must be marked primary");
      return;
    }
    setSaving(true);
    try {
      // Strip the client-side temp ids so the server assigns fresh ones.
      const payload = items.map((c) => {
        const out = { ...c };
        if (out.id && out.id.startsWith("new-")) delete out.id;
        if (!out.email) delete out.email;
        if (!out.address) delete out.address;
        return out;
      });
      await api.post(`/individuals/${row.id}/emergency-contacts`, payload);
      toast.success("Emergency contacts updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="editor-contacts-form">
      <div className="text-xs text-muted-foreground bg-slate-50 border border-slate-200 rounded p-3">
        The complete list is saved together. To remove a contact, use the trash icon; unchanged entries are preserved.
      </div>
      {items.length === 0 && (
        <div className="text-sm text-muted-foreground py-6 text-center border border-dashed rounded"
          data-testid="contacts-empty">
          No emergency contacts on file yet.
        </div>
      )}
      {items.map((c, i) => (
        <div key={c.id || i} className="p-4 border border-border rounded"
          data-testid={`contact-row-${i}`}>
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Name"         value={c.name}         onChange={(v) => setField(i, "name", v)}         testid={`contact-${i}-name`} />
            <TextField label="Relationship" value={c.relationship} onChange={(v) => setField(i, "relationship", v)} testid={`contact-${i}-relationship`} />
            <TextField label="Phone"        value={c.phone}        onChange={(v) => setField(i, "phone", v)}        testid={`contact-${i}-phone`} />
            <TextField label="Email"        value={c.email || ""}  onChange={(v) => setField(i, "email", v)}        type="email" testid={`contact-${i}-email`} />
            <TextField label="Address"      value={c.address || ""} onChange={(v) => setField(i, "address", v)}    span2 testid={`contact-${i}-address`} />
          </div>
          <div className="flex items-center gap-4 mt-3 text-sm">
            <label className="flex items-center gap-2">
              <input type="radio" name="primary-ec"
                checked={!!c.is_primary}
                onChange={() => makePrimary(i)}
                data-testid={`contact-${i}-primary`} />
              Primary contact
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={!!c.is_guardian}
                onChange={(e) => setField(i, "is_guardian", e.target.checked)}
                data-testid={`contact-${i}-guardian`} />
              Guardian
            </label>
            <button type="button" onClick={() => rmRow(i)}
              className="ml-auto text-rose-600 hover:text-rose-800 flex items-center gap-1 text-xs"
              data-testid={`contact-${i}-remove`}>
              <Trash2 size={14} /> Remove
            </button>
          </div>
        </div>
      ))}
      <div className="flex items-center justify-between pt-2">
        <button type="button" onClick={addRow} className="accp-btn-secondary flex items-center gap-1"
          data-testid="contacts-add-btn">
          <Plus size={14} /> Add contact
        </button>
        <button onClick={submit} disabled={saving} className="accp-btn-primary"
          data-testid="contacts-save-btn">
          {saving ? "Saving…" : "Save all contacts"}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// External identifiers — add + deprecate. Existing values are read-only.
// ---------------------------------------------------------------------------
function IdentifiersTab({ row, onSaved }) {
  const [add, setAdd] = useState({
    system: "", identifier_type: "", value: "",
    is_primary_for_system: false, notes: "",
  });
  const [saving, setSaving] = useState(false);
  const items = row.external_identifiers || [];

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!add.system.trim() || !add.identifier_type.trim() || !add.value.trim()) {
      toast.error("System, identifier type, and value are required");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/individuals/${row.id}/external-identifiers`, {
        system: add.system.trim(),
        identifier_type: add.identifier_type.trim(),
        value: add.value.trim(),
        is_primary_for_system: add.is_primary_for_system,
        notes: add.notes.trim() || null,
      });
      toast.success("Identifier added");
      setAdd({ system: "", identifier_type: "", value: "",
                is_primary_for_system: false, notes: "" });
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const deprecate = async (ext) => {
    if (!window.confirm(`Deprecate ${ext.system} / ${ext.identifier_type}? The identifier will be marked inactive but remain in history.`)) return;
    try {
      await api.post(`/individuals/${row.id}/external-identifiers/${ext.id}/deprecate`);
      toast.success("Identifier deprecated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="editor-identifiers-form">
      <div className="text-xs text-muted-foreground bg-slate-50 border border-slate-200 rounded p-3">
        Identifier values cannot be edited once saved. If a value is wrong, deprecate the entry and add a new one with the correct value.
      </div>
      <div>
        <div className="accp-label mb-2">On file</div>
        {items.length === 0 && (
          <div className="text-sm text-muted-foreground py-6 text-center border border-dashed rounded"
            data-testid="identifiers-empty">
            No identifiers on file.
          </div>
        )}
        {items.map((ext) => (
          <div key={ext.id} className={`p-3 border rounded mb-2 text-sm ${ext.deprecated ? "opacity-50 bg-slate-50" : ""}`}
            data-testid={`identifier-row-${ext.id}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-semibold">{ext.system} · {ext.identifier_type}</div>
                <div className="font-mono text-xs mt-1">{ext.value}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {ext.is_primary_for_system && !ext.deprecated && <span className="mr-2 text-emerald-700">Primary</span>}
                  {ext.deprecated && <span className="text-rose-700">Deprecated</span>}
                </div>
              </div>
              {!ext.deprecated && (
                <button type="button" onClick={() => deprecate(ext)}
                  className="text-rose-600 hover:text-rose-800 text-xs flex items-center gap-1"
                  data-testid={`identifier-deprecate-${ext.id}`}>
                  <Trash2 size={14} /> Deprecate
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      <form onSubmit={submitAdd} className="border-t pt-4 space-y-3">
        <div className="accp-label">Add identifier</div>
        <div className="grid grid-cols-2 gap-3">
          <TextField label="System"          value={add.system}          onChange={(v) => setAdd({ ...add, system: v })}          testid="ident-add-system" />
          <TextField label="Identifier type" value={add.identifier_type} onChange={(v) => setAdd({ ...add, identifier_type: v })} testid="ident-add-type" />
          <TextField label="Value"           value={add.value}           onChange={(v) => setAdd({ ...add, value: v })}           testid="ident-add-value" />
          <TextField label="Notes"           value={add.notes}           onChange={(v) => setAdd({ ...add, notes: v })}           testid="ident-add-notes" />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={add.is_primary_for_system}
            onChange={(e) => setAdd({ ...add, is_primary_for_system: e.target.checked })}
            data-testid="ident-add-primary" />
          Mark as primary for this system
        </label>
        <div className="flex justify-end">
          <button className="accp-btn-primary" disabled={saving} data-testid="ident-add-submit">
            {saving ? "Adding…" : "Add identifier"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Service authorizations — replace-list. Editor loads all, allows
// add / edit / end (set effective_end today), then submits the full list.
// ---------------------------------------------------------------------------
function AuthorizationsTab({ row, onSaved }) {
  const [items, setItems] = useState(
    (row.service_authorizations || []).map((a) => ({ ...a,
      service_types: (a.service_types || []).join(", "),
      proc_codes: (a.proc_codes || []).join(", "),
    }))
  );
  const [saving, setSaving] = useState(false);

  const addRow = () =>
    setItems([...items, {
      id: `new-${Date.now()}`,
      waiver_code: "",
      funding_source_code: "",
      service_types: "",
      authorized_units_weekly: "",
      effective_start: new Date().toISOString().slice(0, 10),
      effective_end: "",
      proc_codes: "",
      notes: "",
    }]);
  const setField = (idx, field, value) =>
    setItems(items.map((a, i) => (i === idx ? { ...a, [field]: value } : a)));
  const endToday = (idx) =>
    setField(idx, "effective_end", new Date().toISOString().slice(0, 10));
  const rmRow = (idx) =>
    setItems(items.filter((_, i) => i !== idx));

  const submit = async () => {
    for (const a of items) {
      if (!a.waiver_code.trim() || !a.funding_source_code.trim() || !a.effective_start) {
        toast.error("Each authorization needs waiver code, funding source, and effective start date");
        return;
      }
    }
    setSaving(true);
    try {
      const payload = items.map((a) => {
        const out = {
          waiver_code: a.waiver_code.trim(),
          funding_source_code: a.funding_source_code.trim(),
          service_types: a.service_types.split(",").map((s) => s.trim()).filter(Boolean),
          proc_codes:    a.proc_codes.split(",").map((s) => s.trim()).filter(Boolean),
          effective_start: a.effective_start,
        };
        if (a.effective_end) out.effective_end = a.effective_end;
        if (a.authorized_units_weekly !== "" && a.authorized_units_weekly !== null) {
          out.authorized_units_weekly = Number(a.authorized_units_weekly);
        }
        if (a.notes && a.notes.trim()) out.notes = a.notes.trim();
        // Preserve original id on existing rows so the server keeps the
        // stable identifier used by segments.
        if (a.id && !a.id.startsWith("new-")) out.id = a.id;
        return out;
      });
      await api.post(`/individuals/${row.id}/service-authorizations`, payload);
      toast.success("Service authorizations updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="editor-authorizations-form">
      <div className="text-xs text-muted-foreground bg-slate-50 border border-slate-200 rounded p-3">
        The complete list is saved together. To end an authorization, set its end date (there is a shortcut button). Unchanged authorizations remain intact.
      </div>
      {items.length === 0 && (
        <div className="text-sm text-muted-foreground py-6 text-center border border-dashed rounded"
          data-testid="authorizations-empty">
          No service authorizations on file yet.
        </div>
      )}
      {items.map((a, i) => (
        <div key={a.id || i} className="p-4 border border-border rounded"
          data-testid={`authorization-row-${i}`}>
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Waiver code"          value={a.waiver_code}          onChange={(v) => setField(i, "waiver_code", v)}          testid={`auth-${i}-waiver`} />
            <TextField label="Funding source code" value={a.funding_source_code} onChange={(v) => setField(i, "funding_source_code", v)} testid={`auth-${i}-fs`} />
            <TextField label="Service types (comma-separated)"       value={a.service_types}     onChange={(v) => setField(i, "service_types", v)}     span2 testid={`auth-${i}-services`} />
            <TextField label="Weekly authorized units" type="number" value={a.authorized_units_weekly} onChange={(v) => setField(i, "authorized_units_weekly", v)} testid={`auth-${i}-units`} />
            <TextField label="Procedure codes (comma-separated)"     value={a.proc_codes}        onChange={(v) => setField(i, "proc_codes", v)}       testid={`auth-${i}-procs`} />
            <TextField label="Effective start" type="date"  value={a.effective_start} onChange={(v) => setField(i, "effective_start", v)} testid={`auth-${i}-start`} />
            <TextField label="Effective end"   type="date"  value={a.effective_end || ""} onChange={(v) => setField(i, "effective_end", v)} testid={`auth-${i}-end`} />
            <TextField label="Notes"           value={a.notes || ""} onChange={(v) => setField(i, "notes", v)} span2 testid={`auth-${i}-notes`} />
          </div>
          <div className="flex items-center gap-3 mt-3">
            {!a.effective_end && (
              <button type="button" onClick={() => endToday(i)}
                className="text-amber-700 hover:text-amber-900 text-xs"
                data-testid={`auth-${i}-end-today`}>End effective today</button>
            )}
            <button type="button" onClick={() => rmRow(i)}
              className="ml-auto text-rose-600 hover:text-rose-800 flex items-center gap-1 text-xs"
              data-testid={`auth-${i}-remove`}>
              <Trash2 size={14} /> Remove
            </button>
          </div>
        </div>
      ))}
      <div className="flex items-center justify-between pt-2">
        <button type="button" onClick={addRow} className="accp-btn-secondary flex items-center gap-1"
          data-testid="authorizations-add-btn">
          <Plus size={14} /> Add authorization
        </button>
        <button onClick={submit} disabled={saving} className="accp-btn-primary"
          data-testid="authorizations-save-btn">
          {saving ? "Saving…" : "Save all authorizations"}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Assigned DSPs — view list, grant, revoke.
// ---------------------------------------------------------------------------
function DspsTab({ row, onSaved }) {
  const [data, setData] = useState(null);
  const [allDsps, setAllDsps] = useState([]);
  const [choice, setChoice] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get(`/individuals/${row.id}/assigned-dsps`).then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
    api.get("/users", { params: { role: "dsp", limit: 500 } }).then((r) => {
      // Backend uses `paginate_envelope` → { data, total, page, limit, pages }.
      // Accept bare-array and `items` shapes too so this parser stays valid
      // if the endpoint contract ever normalizes.
      const items = Array.isArray(r.data)
        ? r.data
        : (r.data && (r.data.data || r.data.items)) || [];
      setAllDsps(items.filter((u) => u.active));
    }).catch(() => setAllDsps([]));
  };
  useEffect(load, [row.id]);

  const grant = async () => {
    if (!choice) { toast.error("Select a DSP first"); return; }
    setSaving(true);
    try {
      await api.post(`/individuals/${row.id}/authorize-dsp`,
                      { dsp_user_id: choice, reason: reason.trim() || null });
      toast.success("DSP authorized");
      setChoice(""); setReason("");
      load();
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const revoke = async (dsp) => {
    if (!window.confirm(`Revoke authorization for ${dsp.name || dsp.code}? They will no longer be schedulable for this participant.`)) return;
    try {
      await api.delete(`/individuals/${row.id}/authorize-dsp/${dsp.id}`);
      toast.success("DSP authorization revoked");
      load();
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const authorizedIds = new Set((data?.authorized_dsps || []).map((d) => d.id));
  const grantOptions = allDsps.filter((d) => !authorizedIds.has(d.id));

  return (
    <div className="space-y-4" data-testid="editor-dsps-form">
      <div className="text-xs text-muted-foreground bg-slate-50 border border-slate-200 rounded p-3">
        Authorized DSPs are the only staff who may be scheduled to deliver participant services for this individual.
      </div>
      <div>
        <div className="accp-label mb-2">Currently authorized</div>
        {data?.authorized_dsps?.length ? (
          <div className="border border-border rounded overflow-hidden">
            {data.authorized_dsps.map((d) => (
              <div key={d.id} className="p-3 border-b last:border-b-0 flex items-center justify-between"
                data-testid={`dsp-authorized-${d.id}`}>
                <div>
                  <div className="font-medium">{d.name || d.code}</div>
                  <div className="text-xs text-muted-foreground font-mono">{d.code}</div>
                </div>
                <button type="button" onClick={() => revoke(d)}
                  className="text-rose-600 hover:text-rose-800 text-xs flex items-center gap-1"
                  data-testid={`dsp-revoke-${d.id}`}>
                  <Trash2 size={14} /> Revoke
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-muted-foreground py-4 text-center border border-dashed rounded"
            data-testid="dsps-none-authorized">
            No DSPs are currently authorized to serve this participant.
          </div>
        )}
      </div>
      <div className="border-t pt-4">
        <div className="accp-label mb-2">Authorize a DSP</div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="accp-label text-xs">Select DSP</label>
            <select className="accp-input mt-1 w-full"
              value={choice}
              onChange={(e) => setChoice(e.target.value)}
              data-testid="dsp-grant-select">
              <option value="">— choose —</option>
              {grantOptions.map((d) => (
                <option key={d.id} value={d.id}>{d.name || d.code} ({d.code})</option>
              ))}
            </select>
          </div>
          <TextField label="Reason (optional)" value={reason} onChange={setReason}
            testid="dsp-grant-reason" />
        </div>
        <div className="flex justify-end mt-3">
          <button onClick={grant} disabled={saving} className="accp-btn-primary"
            data-testid="dsp-grant-submit">
            {saving ? "Granting…" : "Authorize DSP"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status — discharge / reactivate / mark-deceased. Reactivate surfaces the
// activation-prerequisite message from the existing endpoint (single source
// of truth — do not invent a parallel rule).
// ---------------------------------------------------------------------------
function StatusTab({ row, onSaved }) {
  const [reason, setReason] = useState("");
  const [effective, setEffective] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  const call = async (endpoint, verb, requireReason = true) => {
    if (requireReason && !reason.trim()) {
      toast.error("A reason is required for this status change");
      return;
    }
    if (!window.confirm(`Confirm: ${verb} this individual (effective ${effective})?`)) return;
    setSaving(true);
    try {
      await api.post(`/individuals/${row.id}/${endpoint}`,
                      { reason: reason.trim() || "operator action", effective_date: effective });
      toast.success(`Individual ${verb}d`);
      setReason("");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const canReactivate = row.status !== "active" && row.status !== "deceased";
  const canDischarge  = row.status === "active" || row.status === "intake";
  const canMarkDeceased = row.status !== "deceased";

  return (
    <div className="space-y-4" data-testid="editor-status-form">
      <div className="p-3 rounded border border-border bg-slate-50 text-sm">
        <div className="accp-label">Current status</div>
        <div className="text-xl font-semibold mt-1 uppercase">{row.status}</div>
      </div>
      {row.status !== "active" && (
        <div className="p-3 rounded bg-amber-50 border border-amber-200 text-sm flex items-start gap-2"
          data-testid="status-activation-guidance">
          <ShieldCheck size={16} className="text-amber-700 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-amber-900 mb-1">Activation prerequisites</div>
            <div className="text-amber-900">
              To transition to <em>active</em>, this individual must have at least one service authorization,
              at least one primary emergency contact, and one active primary external identifier.
              If any prerequisite is missing, the reactivation attempt will report it clearly.
            </div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <TextField label="Effective date" type="date" value={effective} onChange={setEffective}
          testid="status-effective" />
        <TextField label="Reason" value={reason} onChange={setReason}
          testid="status-reason" />
      </div>
      <div className="flex flex-wrap gap-3 pt-2 border-t">
        {canReactivate && (
          <button className="accp-btn-primary" disabled={saving}
            onClick={() => call("reactivate", "activate")}
            data-testid="status-reactivate">Activate</button>
        )}
        {canDischarge && (
          <button className="accp-btn-secondary" disabled={saving}
            onClick={() => call("discharge", "discharge")}
            data-testid="status-discharge">Discharge</button>
        )}
        {canMarkDeceased && (
          <button className="accp-btn-secondary text-rose-700" disabled={saving}
            onClick={() => call("mark-deceased", "mark deceased")}
            data-testid="status-mark-deceased">Mark deceased</button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small primitives — kept local so this file can be dropped in without
// disturbing shared components.
// ---------------------------------------------------------------------------
function TextField({ label, value, onChange, span2 = false, type = "text", testid }) {
  return (
    <div className={span2 ? "col-span-2" : ""}>
      <label className="accp-label text-xs">{label}</label>
      <input className="accp-input mt-1 w-full" type={type} value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid} />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="accp-label text-xs">{label}</label>
      <select className="accp-input mt-1 w-full" value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}>
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </div>
  );
}

function AddressGrid({ addr, onChange, prefix }) {
  const set = (k, v) => onChange({ ...addr, [k]: v });
  return (
    <div className="grid grid-cols-2 gap-3">
      <TextField label="Street" value={addr.street || ""} onChange={(v) => set("street", v)} testid={`${prefix}-street`} />
      <TextField label="Unit"   value={addr.unit || ""}   onChange={(v) => set("unit", v)}   testid={`${prefix}-unit`} />
      <TextField label="City"   value={addr.city || ""}   onChange={(v) => set("city", v)}   testid={`${prefix}-city`} />
      <TextField label="State"  value={addr.state || ""}  onChange={(v) => set("state", v)}  testid={`${prefix}-state`} />
      <TextField label="ZIP"    value={addr.zip || ""}    onChange={(v) => set("zip", v)}    testid={`${prefix}-zip`} />
      <TextField label="County" value={addr.county || ""} onChange={(v) => set("county", v)} testid={`${prefix}-county`} />
    </div>
  );
}
